import { lazy, Suspense } from 'react';
import { Navigate, Outlet, RouteObject, useRoutes } from 'react-router';

import { HandleUserRole } from '@bai/features/user';
import { RoleNames } from '@bai/utils/types';

import { PageLayout } from '../../../../libs/components/main/src/lib/page-layout/page-layout';
import { Spinner } from '../../../../libs/components/main/src/lib/spinner/spinner';
// Import LoginPageData DIRECTLY from its source file rather than through the
// @bai/features barrel (index.ts). The barrel re-exports 50+ modules and even
// with tree shaking webpack must evaluate every re-exported module to build the
// module graph. A direct path import means ONLY the login feature code is
// included in the initial bundle.
import { LoginPageData } from '../../../../libs/features/main/src/lib/login/LoginPageData';

// ---------------------------------------------------------------------------
// All other route-level components are lazy-loaded from a separate async chunk
// ("app-features").  This keeps the initial login-page bundle small and defers
// loading authenticated-route code until the user actually navigates there.
// Each explicit lazy() call preserves the component's TypeScript prop types.
// ---------------------------------------------------------------------------
const lf = () =>
  import(/* webpackChunkName: "app-features" */ './lazyFeatures');

// Catalog chunk — only loaded when navigating to catalog/, documents-catalog/, or search paths.
const lca = () =>
  import(/* webpackChunkName: "app-catalog" */ './lazyCatalogFeatures');

// Admin chunk — only loaded when navigating to admin/ or admin-react/ paths.
const lad = () =>
  import(/* webpackChunkName: "app-admin" */ './lazyAdminFeatures');

const AdminContent = lazy(() =>
  lad().then((m) => ({ default: m.AdminContent })),
);
const AdminDashboard = lazy(() =>
  lad().then((m) => ({ default: m.AdminDashboard })),
);
const AdminRecommendations = lazy(() =>
  lad().then((m) => ({ default: m.AdminRecommendations })),
);
const AdminRules = lazy(() => lad().then((m) => ({ default: m.AdminRules })));
const BreadcrumbProvider = lazy(() =>
  lf().then((m) => ({ default: m.BreadcrumbProvider })),
);
const CatalogBrowse = lazy(() =>
  lca().then((m) => ({ default: m.CatalogBrowse })),
);
const Certificate = lazy(() => lf().then((m) => ({ default: m.Certificate })));
const ChangePasswordData = lazy(() =>
  lf().then((m) => ({ default: m.ChangePasswordData })),
);
const ContentEdit = lazy(() => lad().then((m) => ({ default: m.ContentEdit })));
const ContextBreadcrumb = lazy(() =>
  lf().then((m) => ({ default: m.ContextBreadcrumb })),
);
const CookieConsent = lazy(() =>
  lca().then((m) => ({ default: m.CookieConsent })),
);
const Dashboard = lazy(() => lf().then((m) => ({ default: m.Dashboard })));
const DelegateToLegacy = lazy(() =>
  lf().then((m) => ({ default: m.DelegateToLegacy })),
);
const EditProfileData = lazy(() =>
  lf().then((m) => ({ default: m.EditProfileData })),
);
const EnrollmentApprovalsData = lazy(() =>
  lf().then((m) => ({ default: m.EnrollmentApprovalsData })),
);
const Footer = lazy(() => lf().then((m) => ({ default: m.Footer })));
const Header = lazy(() => lf().then((m) => ({ default: m.Header })));
const Help = lazy(() => lf().then((m) => ({ default: m.Help })));
const HelpVideo = lazy(() => lf().then((m) => ({ default: m.HelpVideo })));
const ILTCalendar = lazy(() => lf().then((m) => ({ default: m.ILTCalendar })));
const InboxData = lazy(() => lf().then((m) => ({ default: m.InboxData })));
const InstructorHelp = lazy(() =>
  lf().then((m) => ({ default: m.InstructorHelp })),
);
const ManagerHelp = lazy(() => lf().then((m) => ({ default: m.ManagerHelp })));
const MessagesData = lazy(() =>
  lf().then((m) => ({ default: m.MessagesData })),
);
const Product = lazy(() => lca().then((m) => ({ default: m.Product })));
const ProfileHeaderData = lazy(() =>
  lf().then((m) => ({ default: m.ProfileHeaderData })),
);
const PublicCatalogBody = lazy(() =>
  lca().then((m) => ({ default: m.PublicCatalogBody })),
);
const RequestsData = lazy(() =>
  lf().then((m) => ({ default: m.RequestsData })),
);
const SearchResults = lazy(() =>
  lca().then((m) => ({ default: m.SearchResults })),
);
const StudentHelp = lazy(() => lf().then((m) => ({ default: m.StudentHelp })));
const TabHeader = lazy(() => lad().then((m) => ({ default: m.TabHeader })));
const TaHelp = lazy(() => lf().then((m) => ({ default: m.TaHelp })));
const TaskApprovalsData = lazy(() =>
  lf().then((m) => ({ default: m.TaskApprovalsData })),
);
const TrainingInsights = lazy(() =>
  lf().then((m) => ({ default: m.TrainingInsights })),
);
const Transcripts = lazy(() => lf().then((m) => ({ default: m.Transcripts })));
const UserProfile = lazy(() => lf().then((m) => ({ default: m.UserProfile })));

function getCatalogReact(isPublic: boolean) {
  return {
    path: isPublic ? '' : 'catalog/',
    element: isPublic ? (
      <>
        <Header isPublic />
        <Outlet />
        <CookieConsent />
        <Footer />
      </>
    ) : (
      <>
        <Header />
        <HandleUserRole baiRole={RoleNames.Student} />
        <Outlet />
        <Footer />
      </>
    ),
    children: isPublic
      ? [
          {
            path: '',
            index: true,
            element: <PublicCatalogBody />,
          },
          {
            path: 'browse/:category/:subcategory',
            index: true,
            element: <CatalogBrowse isPublic level={3} />,
          },
          {
            path: 'browse/:category',
            index: true,
            element: <CatalogBrowse isPublic level={2} />,
          },
          {
            path: 'browse',
            index: true,
            element: <CatalogBrowse isPublic level={1} />,
          },
          {
            path: 'product/:urlEncodedCodeAndName',
            index: true,
            element: (
              <BreadcrumbProvider>
                <ContextBreadcrumb />
                <PageLayout>
                  <Product isPublic />
                </PageLayout>
              </BreadcrumbProvider>
            ),
          },
          {
            path: 'search/:catalog/:category/:subcategory',
            index: true,
            element: <SearchResults isPublic />,
          },
          {
            path: 'search/:catalog/:category',
            index: true,
            element: <SearchResults isPublic />,
          },
          {
            path: 'search/:catalog',
            index: true,
            element: <SearchResults isPublic isLevel1 />,
          },
          {
            path: 'search',
            index: true,
            element: <SearchResults isFromSearchHeader isPublic />,
          },
        ]
      : [
          {
            path: 'search/:catalog/:category/:subcategory',
            index: true,
            element: <SearchResults />,
          },
          {
            path: 'search/:catalog/:category',
            index: true,
            element: <SearchResults />,
          },
          {
            path: 'search/:catalog',
            index: true,
            element: <SearchResults isLevel1 />,
          },
          {
            path: 'search',
            index: true,
            element: <SearchResults isFromSearchHeader />,
          },
          {
            path: 'product/:productId',
            index: true,
            element: (
              <BreadcrumbProvider>
                <ContextBreadcrumb />
                <PageLayout>
                  <Product />
                </PageLayout>
              </BreadcrumbProvider>
            ),
          },
          {
            path: ':category/:subcategory',
            index: true,
            element: <CatalogBrowse level={3} />,
          },
          {
            path: ':category',
            index: true,
            element: <CatalogBrowse level={2} />,
          },
          {
            path: '',
            index: true,
            element: <CatalogBrowse level={1} />,
          },
        ],
  };
}

function getDocumentsCatalogReact() {
  return {
    ...getCatalogReact(false),
    path: 'documents-catalog/',
  };
}

export const routeConfig: RouteObject[] = [
  {
    path: '/',
    element: <LoginPageData />,
  },
  {
    path: 'admin/',
    element: (
      <>
        <Header />
        <HandleUserRole baiRole={RoleNames.TA} />
        <Outlet />
      </>
    ),
    children: [
      {
        path: 'messages',
        element: (
          <BreadcrumbProvider>
            <ContextBreadcrumb />
            <PageLayout>
              <InboxData />
              <Footer />
            </PageLayout>
          </BreadcrumbProvider>
        ),
        children: [
          {
            path: '',
            element: <Navigate replace to="requests" />,
          },
          {
            path: 'requests',
            element: <RequestsData />,
          },
          {
            path: 'enrollments',
            element: <EnrollmentApprovalsData />,
          },
          {
            path: 'tasks',
            element: <TaskApprovalsData />,
          },
        ],
      },
    ],
  },
  {
    path: 'courses/',
    element: (
      <>
        <Header />
        <HandleUserRole baiRole={RoleNames.Student} />
        <Outlet />
      </>
    ),
    children: [
      {
        path: '',
        element: <Navigate replace to="certificate" />,
      },
      {
        path: 'certificate',
        element: (
          <BreadcrumbProvider>
            <ContextBreadcrumb />
            <Certificate />
            <Footer />
          </BreadcrumbProvider>
        ),
        children: [
          {
            path: '',
            element: <Navigate replace to="assignments" />,
          },
          {
            path: 'assignments/:transcriptId',
            element: <Certificate />,
          },
        ],
      },
    ],
  },
  getCatalogReact(false),
  getDocumentsCatalogReact(),
  {
    path: 'student/',
    element: (
      <>
        <Header />
        <HandleUserRole baiRole={RoleNames.Student} />
        <Outlet />
      </>
    ),
    children: [
      {
        path: '',
        element: <Navigate replace to="home" />,
      },
      {
        path: 'home',
        element: (
          <BreadcrumbProvider>
            <Dashboard />
            <Footer />
          </BreadcrumbProvider>
        ),
      },
      {
        path: 'profile',
        element: (
          <BreadcrumbProvider>
            <ContextBreadcrumb />
            <PageLayout>
              <ProfileHeaderData />
              <Outlet />
              <Footer />
            </PageLayout>
          </BreadcrumbProvider>
        ),
        children: [
          {
            path: '',
            element: <UserProfile />,
          },
          {
            path: 'edit-profile',
            element: <EditProfileData />,
          },
          {
            path: 'change-password',
            element: <ChangePasswordData />,
          },
        ],
      },
      {
        path: 'messages',
        element: (
          <BreadcrumbProvider>
            <ContextBreadcrumb />
            <PageLayout>
              <InboxData />
              <Footer />
            </PageLayout>
          </BreadcrumbProvider>
        ),
        children: [
          {
            path: '',
            element: <Navigate replace to="inbox" />,
          },
          {
            path: 'inbox',
            index: true,
            element: <MessagesData isPublic />,
          },
        ],
      },
    ],
  },
  {
    path: 'admin/',
    element: (
      <>
        <TabHeader />
        <HandleUserRole baiRole={RoleNames.TA} allowAuthor />
        <Outlet />
        <Footer />
      </>
    ),
    children: [
      {
        path: 'dashboard',
        element: <AdminDashboard />,
      },
      {
        path: 'content',
        element: <AdminContent />,
      },
      {
        path: 'content/edit/:productId',
        element: <ContentEdit />,
      },
      {
        path: 'rules',
        element: <AdminRules />,
      },
      {
        path: 'recommendations',
        element: <AdminRecommendations />,
      },
    ],
  },
  {
    path: 'student/',
    element: (
      <>
        <Header />
        <Outlet />
        <Footer />
      </>
    ),
    children: [
      {
        path: 'transcripts',
        element: (
          <BreadcrumbProvider>
            <ContextBreadcrumb />
            <Transcripts />
          </BreadcrumbProvider>
        ),
      },
      {
        path: 'ilt-calendar',
        element: (
          <BreadcrumbProvider>
            <ContextBreadcrumb />
            <ILTCalendar />
          </BreadcrumbProvider>
        ),
      },
    ],
  },
  {
    path: 'help',
    element: (
      <>
        <Header />
        <Outlet />
        <Footer />
      </>
    ),
    children: [
      {
        index: true,
        element: <Help />,
      },
      {
        path: 'student',
        element: <StudentHelp />,
      },
      {
        path: 'instructor',
        element: <InstructorHelp />,
      },
      {
        path: 'traininginsights',
        element: <TrainingInsights />,
      },
      {
        path: 'ta',
        element: <TaHelp />,
      },
      {
        path: 'manager',
        element: <ManagerHelp />,
      },
      {
        path: 'studentdemovideo',
        element: (
          <HelpVideo
            video={{
              name: 'Student Demo Video',
              src: 'https://ld-connect.bai.org/wp-content/uploads/2025/02/LMS-Student-Demo-Video-2-Optimized.mp4',
            }}
          />
        ),
      },
      {
        path: 'instructordemovideo',
        element: (
          <HelpVideo
            video={{
              name: 'Instructor Demo Video',
              src: 'https://www.bai.org/lmsvideos/bai_lms_instructor_demo.mp4',
            }}
          />
        ),
      },
      {
        path: 'traininginsightsdemovideo',
        element: (
          <HelpVideo
            video={{
              name: 'Training Insights Demo Video',
              src: 'https://ld-connect.bai.org/wp-content/uploads/2026/09/Create-Decision-Report-DMs-2.mp4',
            }}
          />
        ),
      },
      {
        path: 'managerdemovideo',
        element: (
          <HelpVideo
            video={{
              name: 'Manager Demo Video',
              src: 'https://ld-connect.bai.org/wp-content/uploads/2020/08/Manager-Demo-Video.mp4',
            }}
          />
        ),
      },
    ],
  },
  {
    path: '*',
    element: <DelegateToLegacy />,
  },
];

export const publicRouteConfig: RouteObject[] = [
  getCatalogReact(true),
  {
    path: '*',
    element: <DelegateToLegacy />,
  },
];

const testIsPublic = () => {
  const catalogRegex = /^catalog[.-]/i; // hostname begins with catalog. or catalog- case insensitive
  return catalogRegex.test(window.location.hostname);
};

export const getRouteConfig = () =>
  testIsPublic() ? publicRouteConfig : routeConfig;

export const getCombinedRouteConfigs = () =>
  routeConfig.filter((item) => item.path !== '*').concat(publicRouteConfig);

export default function AppRoutes() {
  const routes = useRoutes(testIsPublic() ? publicRouteConfig : routeConfig);
  return <Suspense fallback={<Spinner />}>{routes}</Suspense>;
}

export function AppRouter() {
  return testIsPublic() ? publicRouteConfig : routeConfig;
}
